import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { useParams } from "react-router";
import SideBar from "components/common/SideBar/SideBarNew";
import TableHeader from "components/common/Table/TableHeader";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import { LoginContext } from "helpers/LoginContext";
import {
  ROSTERING_INVOICING_BREADCRUMB,
  ROSTERING_INVOICING_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import { useDispatch, useSelector } from "react-redux";
import { spRosteringSettingsRequest } from "store/actions/sp.actions";
import { getSpRosteringSetting } from "store/selector/sp.selector";
import { apiGetRequest, apiDeleteRequest } from "helpers/Requests";
import { URL_ROSTERING_INVOICE_DETAIL, URL_ROSTER_INVOICE_OTHER_LINES } from "store/services/URL";
import InvoicePreviewTable from "./InvoicePreviewTable";
import OtherLinesModal from "./OtherLinesModal";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import { BiPencil, BiTrash } from "react-icons/bi";
import { showClaimTypeInScheduleTimesheets } from "pages/Rostering/WorkerShift/components/advanceModeSplitDefaults";
import { parseBooleanVal } from "Utils/utils";


const RosterInvoicePreview = () => {
  const { sideBarPadding } = useContext(LoginContext);
  const dispatch = useDispatch();
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoicesDetailList, setInvoiceDetailList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editLine, setEditLine] = useState(null);
  const [deleteLine, setDeleteLine] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { uuid } = useParams();

  useEffect(() => {
    dispatch(spRosteringSettingsRequest());
  }, [dispatch]);

  const showClaimTypeScheduleTimesheets = useMemo(
    () => showClaimTypeInScheduleTimesheets(spRosteringSetting),
    [spRosteringSetting]
  );

  const showTravelToggleScheduleTimesheets = useMemo(() => {
    const s = spRosteringSetting?.show_travel_toggle_schedule_timesheets;
    return parseBooleanVal(s) || s === 1 || String(s) === "1";
  }, [spRosteringSetting]);

  const fetchInvoice = useCallback(() => {
    if (!uuid) return;
    setLoader(true);
    apiGetRequest(`${URL_ROSTERING_INVOICE_DETAIL}?uuid=${uuid}`)
      .then((res) => {
        if (res?.status === 200) {
          const data = res.data?.data;
          setInvoiceData(data);
          setInvoiceDetailList(data?.invoice_detail ?? []);
        }
      })
      .catch((error) => console.log({ error }))
      .finally(() => setLoader(false));
  }, [uuid]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const invoiceDetailRows = invoicesDetailList ?? [];

  const isDraft = invoiceData?.status === "Extract Generated";
  const otherLines = invoiceData?.other_lines ?? [];

  const handleDeleteConfirm = () => {
    if (!deleteLine || !uuid) return;
    setDeleting(true);
    apiDeleteRequest(`${URL_ROSTER_INVOICE_OTHER_LINES(uuid)}/${deleteLine.uuid}`)
      .then((res) => {
        if (res?.status === 200) {
          toast.success("Line removed.");
          setDeleteLine(null);
          fetchInvoice();
        } else {
          toast.error(res?.data?.message || "Failed to remove line.");
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to remove line."))
      .finally(() => setDeleting(false));
  };

  return (
    <div className='min-h-full'>
      <SideBar />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        }  flex flex-col`}
      >
        <main className='h-screen flex-1 bg-white'>
          <Header
            title='Invoice Preview'
            breadcrumb={
              <Breadcrumb
                pages={ROSTERING_INVOICING_BREADCRUMB(
                  ROSTERING_INVOICING_BREADCRUMB_PAGES.ROSTER_INVOICE_PREVIEW
                )}
              />
            }
          />
          <div className='w-full bg-white'>
            <div className='sm:px-6 lg:px-4 py-4'>
              <div className='flex flex-col'>
                <div className='min-w-full overflow-hidden rounded-xl border divide-gray-400'>
                  <div className='shadow ring-1 ring-black ring-opacity-5'>
                    <div className='table-wrp block overflow-auto max-h-[calc(100vh-100px)] custom-scroll'>
                      <table className='min-w-full divide-y border divide-gray-400'>
                        <thead className='sticky top-0 z-[5] bg-white capitalize divide-gray-300 md'>
                        <tr>
                          <TableHeader
                              isSort={false}
                              label={""}
                              cssClass='text-center'
                          />
                          <TableHeader
                              isSort={false}
                              label={"Reference "}
                              cssClass='text-center'
                          />
                          <TableHeader
                              isSort={false}
                              label={"Worker(s)"}
                              cssClass='normal-case text-center'
                          />
                          <TableHeader
                              isSort={false}
                              label={"Work Start time"}
                              cssClass='text-center'
                          />
                          <TableHeader
                              isSort={false}
                              label={"Work end time"}
                              cssClass='text-center'
                          />

                          <TableHeader
                              isSort={false}
                              label={"Ratio"}
                              cssClass='text-center'
                          />

                          <TableHeader
                              isSort={false}
                              label={"Date Delivered"}
                              cssClass='text-center'
                          />
                        </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-200 bg-white'>
                        {loader ? (
                            <tr>
                              <td colSpan={14} className='bg-gray-50 py-2'>
                                <CircularLoader
                                    classes='flex justify-center items-center'
                                    classloader='loader-l'
                                />
                              </td>
                            </tr>
                        ) : invoiceDetailRows &&
                        invoiceDetailRows?.length > 0 ? (
                            invoiceDetailRows?.map(
                                (item, index) => (
                                    <InvoicePreviewTable
                                        item={item}
                                        key={index}
                                        careSector={invoiceData?.care_sector}
                                        showClaimTypeScheduleTimesheets={
                                          showClaimTypeScheduleTimesheets
                                        }
                                        showTravelToggleScheduleTimesheets={
                                          showTravelToggleScheduleTimesheets
                                        }
                                    />
                                )
                            )
                        ) : (
                            (!invoiceDetailRows ||
                                invoiceDetailRows?.length ===
                                0) && (
                                <tr>
                                  <td
                                      colSpan={14}
                                      className='py-6 text-center text-red-600'
                                  >
                                    No Data Found !
                                  </td>
                                </tr>
                            )
                        )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                {!isDraft && invoiceData && (
                  <div className="mt-4 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    This invoice has been issued and cannot be modified.
                  </div>
                )}
                {/* {(invoiceData?.has_other_lines || isDraft) && (
                  <div className='mt-4 min-w-full overflow-hidden rounded-xl border divide-gray-400'>
                    <div className='shadow ring-1 ring-black ring-opacity-5'>
                      <div className='px-3 py-2 bg-gray-100 font-medium flex items-center justify-between'>
                        <span>Other Line Items</span>
                        {isDraft && (
                          <CustomButton type="button" variant="primary" label="Add line" onClick={() => setAddModalOpen(true)} />
                        )}
                      </div>
                      {otherLines.length > 0 ? (
                        <table className='min-w-full divide-y divide-gray-300'>
                          <thead className='bg-gray-50'>
                            <tr>
                              <th className='px-3 py-2 text-left text-xs font-medium text-gray-600'>Code</th>
                              <th className='px-3 py-2 text-left text-xs font-medium text-gray-600'>Description</th>
                              <th className='px-3 py-2 text-left text-xs font-medium text-gray-600'>Funding</th>
                              <th className='px-3 py-2 text-right text-xs font-medium text-gray-600'>Quantity</th>
                              <th className='px-3 py-2 text-right text-xs font-medium text-gray-600'>Rate</th>
                              <th className='px-3 py-2 text-right text-xs font-medium text-gray-600'>Total</th>
                              {isDraft && <th className='px-3 py-2 text-right text-xs font-medium text-gray-600'>Actions</th>}
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-gray-200 bg-white'>
                            {otherLines.map((line, idx) => (
                              <tr key={line.uuid || idx}>
                                <td className='px-3 py-2 text-sm text-gray-700'>{line.code ?? "-"}</td>
                                <td className='px-3 py-2 text-sm text-gray-700'>{line.description}</td>
                                <td className='px-3 py-2 text-sm text-gray-700'>{line.funding_type ?? "-"}</td>
                                <td className='px-3 py-2 text-sm text-right text-gray-700'>{line.quantity}</td>
                                <td className='px-3 py-2 text-sm text-right text-gray-700'>${parseFloat(line.unit_rate || 0).toFixed(2)}</td>
                                <td className='px-3 py-2 text-sm text-right text-gray-700'>${parseFloat(line.line_total || 0).toFixed(2)}</td>
                                {isDraft && (
                                  <td className='px-3 py-2 text-sm text-right'>
                                    <button type="button" className="p-1 text-blue-600 hover:bg-blue-50 rounded" onClick={() => setEditLine(line)} title="Edit">
                                      <BiPencil className="inline h-4 w-4" />
                                    </button>
                                    <button type="button" className="p-1 text-red-600 hover:bg-red-50 rounded ml-1" onClick={() => setDeleteLine(line)} title="Remove">
                                      <BiTrash className="inline h-4 w-4" />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="px-3 py-4 text-sm text-gray-500">No other lines. {isDraft && "Click Add line to add one."}</div>
                      )}
                    </div>
                  </div>
                )}
                {invoiceData && (invoiceData.total_amount != null || invoiceData.total_amount_inc_gst != null) && (
                  <div className='mt-4 flex justify-end'>
                    <div className='rounded border border-gray-200 bg-gray-50 px-4 py-2 min-w-[200px] text-right'>
                      <div className='text-sm text-gray-600'>Subtotal: ${parseFloat(invoiceData.total_amount || 0).toFixed(2)}</div>
                      {invoiceData.total_gst_amount != null && parseFloat(invoiceData.total_gst_amount) !== 0 && (
                        <div className='text-sm text-gray-600'>GST: ${parseFloat(invoiceData.total_gst_amount).toFixed(2)}</div>
                      )}
                      <div className='font-medium mt-1'>
                        Total: ${parseFloat(invoiceData.total_amount_inc_gst ?? invoiceData.total_amount ?? 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )} */}
                <OtherLinesModal
                  open={addModalOpen}
                  onClose={() => setAddModalOpen(false)}
                  mode="add"
                  invoiceUuid={uuid}
                  onSuccess={fetchInvoice}
                />
                <OtherLinesModal
                  open={!!editLine}
                  onClose={() => setEditLine(null)}
                  mode="edit"
                  invoiceUuid={uuid}
                  line={editLine}
                  onSuccess={fetchInvoice}
                />
                {deleteLine && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => !deleting && setDeleteLine(null)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-4" onClick={(e) => e.stopPropagation()}>
                      <p className="text-gray-700 mb-4">Remove this line from the invoice?</p>
                      <div className="flex justify-end gap-2">
                        <CustomButton type="button" variant="secondary" label="Cancel" onClick={() => setDeleteLine(null)} disabled={deleting} />
                        <CustomButton type="button" variant="primary" label={deleting ? "Removing…" : "Remove"} onClick={handleDeleteConfirm} showLoading={deleting} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RosterInvoicePreview;
